import { Button, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { ErrorRetryAlert } from 'components/common/ErrorRetryAlert'
import PageSection from 'components/common/PageSection'
import Icon from 'components/Icon'
import { type Risk } from 'components/support/risks/risk-form-utils'
import { type TestSuiteFormValues } from 'components/test-suites/forms/test-suite-form-utils'
import { useTestSuiteFormContext } from 'components/test-suites/forms/TestSuiteFormContext'
import { TestSuiteRiskList } from 'components/test-suites/forms/TestSuiteRiskList'
import { not, simpleIntersection } from 'utils/list'

const PAGE_TEXT = {
  body: 'You can customize which automatically detected risks contribute to your score. This change will impact all teams that adopt this test suite.',
  bodyOfficial:
    'The detected risks in this test suite cannot be edited. Clone this test suite to make customizations.',
  errorRetryAlert: 'Risks could not be loaded',
  header: 'Contributing Risks',
  risksIgnoredText: 'Risks that will be ignored',
  risksIncludedText: 'Risks included in Score'
} as const

export const TestSuiteRisks: React.FC = () => {
  const {
    canCreateAndEditTestSuites,
    isOfficialTestSuite,
    queryResultTestSuitesDetectedRisks
  } = useTestSuiteFormContext()
  const {
    data: detectedRisks = [],
    isFetching: isFetchingDetectedRisks,
    isError: hasDetectedRisksError,
    refetch: fetchDetectedRisks
  } = queryResultTestSuitesDetectedRisks

  const canEdit = canCreateAndEditTestSuites && !isOfficialTestSuite

  const { getValues, setValue } = useFormContext<TestSuiteFormValues>()
  const excludedRiskIds = getValues('excludedRiskIds')

  const { excludedRisks, includedRisks } = useMemo(() => {
    const excludedRiskIdsSet = new Set(excludedRiskIds)
    const groupedRisks: {
      includedRisks: Risk[]
      excludedRisks: Risk[]
    } = { includedRisks: [], excludedRisks: [] }

    return detectedRisks.reduce((acc, risk) => {
      if (excludedRiskIdsSet.has(risk.id)) {
        acc.excludedRisks.push(risk)
      } else {
        acc.includedRisks.push(risk)
      }

      return acc
    }, groupedRisks)
  }, [detectedRisks, excludedRiskIds])

  const [checked, setChecked] = useState<Risk[]>([])
  const [left, setLeft] = useState<Risk[]>(includedRisks ?? [])
  const [right, setRight] = useState<Risk[]>(excludedRisks ?? [])

  const leftChecked = simpleIntersection(checked, left)
  const rightChecked = simpleIntersection(checked, right)

  useEffect(() => {
    if (left.length === 0 && right.length === 0) {
      if (includedRisks?.length) {
        setLeft(includedRisks)
      }

      if (excludedRisks?.length) {
        setRight(excludedRisks)
      }
    }
  }, [excludedRisks, includedRisks, left.length, right.length])

  const handleToggle = (risk: Risk) => () => {
    const currentIndex = checked.findIndex((c) => c.id === risk.id)
    const newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(risk)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    setChecked(newChecked)
  }

  const handleMoveAllRight = () => {
    setRight(right.concat(left))
    setLeft([])

    setValue(
      'excludedRiskIds',
      detectedRisks.map((risk) => risk.id),
      { shouldDirty: true }
    )
  }

  const handleMoveCheckedRight = () => {
    const newRight = right.concat(leftChecked)
    setRight(newRight)
    setLeft(not(left, leftChecked))
    setChecked(not(checked, leftChecked))

    setValue(
      'excludedRiskIds',
      newRight.map((risk) => risk.id),
      { shouldDirty: true }
    )
  }

  const handleMoveCheckedLeft = () => {
    const newRight = not(right, rightChecked)
    setLeft(left.concat(rightChecked))
    setRight(newRight)
    setChecked(not(checked, rightChecked))

    setValue(
      'excludedRiskIds',
      newRight.map((risk) => risk.id),
      { shouldDirty: true }
    )
  }

  const handleMoveAllLeft = () => {
    setLeft(left.concat(right))
    setRight([])

    setValue('excludedRiskIds', [], { shouldDirty: true })
  }

  return (
    <Stack spacing={4}>
      <Stack>
        <Typography variant="h6">{PAGE_TEXT.header}</Typography>
        <Typography>
          {isOfficialTestSuite ? PAGE_TEXT.bodyOfficial : PAGE_TEXT.body}
        </Typography>
      </Stack>
      <PageSection
        hasError={hasDetectedRisksError}
        isLoading={isFetchingDetectedRisks}
        renderError={() => (
          <ErrorRetryAlert
            alertText={PAGE_TEXT.errorRetryAlert}
            isRetrying={isFetchingDetectedRisks}
            onClick={fetchDetectedRisks}
          />
        )}
      >
        <Stack direction="row" sx={{ alignItems: 'center', columnGap: 2 }}>
          <Stack>
            <Typography>{PAGE_TEXT.risksIncludedText}</Typography>
            <TestSuiteRiskList
              canEdit={canEdit}
              checked={checked}
              detectedRisks={left}
              handleToggle={handleToggle}
            />
          </Stack>
          {canEdit && (
            <Stack>
              <Stack sx={{ alignItems: 'center' }}>
                <Button
                  data-testid="test-suite-risk-move-all-right"
                  disabled={left.length === 0}
                  size="small"
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  onClick={handleMoveAllRight}
                >
                  <Icon icon="solidAnglesRight" />
                </Button>
                <Button
                  data-testid="test-suite-risk-move-checked-right"
                  disabled={leftChecked.length === 0}
                  size="small"
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  onClick={handleMoveCheckedRight}
                >
                  <Icon icon="solidAngleRight" />
                </Button>
                <Button
                  data-testid="test-suite-risk-move-checked-left"
                  disabled={rightChecked.length === 0}
                  size="small"
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  onClick={handleMoveCheckedLeft}
                >
                  <Icon icon="solidAngleLeft" />
                </Button>
                <Button
                  data-testid="test-suite-risk-move-all-left"
                  disabled={right.length === 0}
                  size="small"
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  onClick={handleMoveAllLeft}
                >
                  <Icon icon="solidAnglesLeft" />
                </Button>
              </Stack>
            </Stack>
          )}
          <Stack>
            <Typography>{PAGE_TEXT.risksIgnoredText}</Typography>
            <TestSuiteRiskList
              canEdit={canEdit}
              checked={checked}
              detectedRisks={right}
              handleToggle={handleToggle}
            />
          </Stack>
        </Stack>
      </PageSection>
    </Stack>
  )
}
